import { Info } from "lucide-react";
import type { FC } from "react";

import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { initialsOf } from "../../companies/utils";
import { formatMoney } from "../../timeTracking/utils";
import { usePortal } from "../components/PortalShell";
import { usePortalSpend } from "../queries";
import type { PortalSpend } from "../types";

const COLS =
  "grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.1fr)_100px_90px_120px_120px] items-center gap-3 px-4";

const dash = (n: number) => (n ? formatMoney(n) : "–");

export const spendKpis = (s: PortalSpend) => [
  {
    label: "Budget",
    value: s.budget != null ? formatMoney(s.budget) : "Not set",
    sub: "Immigration, all cost centers"
  },
  {
    label: "Fees to date",
    value: formatMoney(s.billed),
    sub: "From the firm's exported time and fees"
  },
  {
    label: "Work not yet billed",
    value: formatMoney(s.unbilled),
    sub: "Estimate from the firm's time"
  },
  {
    label: "Remaining",
    value: s.remaining != null ? formatMoney(s.remaining) : "–",
    sub: "After unbilled work"
  }
];

export const PortalSpendPage: FC = () => {
  const me = usePortal();
  const { data, isLoading } = usePortalSpend(me.features.spend);
  if (isLoading || !data) return <Skeleton className="h-64" />;
  const centers = data.by_cost_center.filter(
    (c) => c.budget != null || c.billed || c.unbilled
  );
  return (
    <>
      <span className="flex flex-col gap-1">
        <span className="text-xl font-semibold">Spend</span>
        <span className="text-base text-muted-foreground">
          {[
            data.fiscal_year ? `FY${data.fiscal_year}` : "All time",
            data.entities.join(" and ")
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      <div
        className="grid grid-cols-2 gap-3 md:grid-cols-4"
        data-testid="spend-kpis"
      >
        {spendKpis(data).map((k) => (
          <div
            key={k.label}
            className="flex flex-col gap-1 rounded-lg border border-border bg-background p-4"
          >
            <span className="text-xs text-muted-foreground">{k.label}</span>
            <span className="text-xl font-semibold tabular-nums">
              {k.value}
            </span>
            <span className="text-sm text-muted-foreground">{k.sub}</span>
          </div>
        ))}
      </div>

      {centers.length ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4">
          <span className="text-base font-semibold">By cost center</span>
          {centers.map((c) => {
            const used = c.billed + c.unbilled;
            const pct = c.budget ? (used / c.budget) * 100 : 0;
            return (
              <div
                key={c.cost_center || "none"}
                className="grid grid-cols-[200px_minmax(0,1fr)_190px] items-center gap-3.5"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-medium">
                    {c.name || c.cost_center || "No cost center"}
                  </span>
                  {c.name && c.cost_center ? (
                    <span className="font-mono text-xs text-muted-foreground">
                      {c.cost_center}
                    </span>
                  ) : null}
                </span>
                {c.budget ? (
                  <Progress
                    value={Math.min(100, pct)}
                    className={cn(pct > 85 && "[&>div]:bg-warn")}
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">
                    No budget
                  </span>
                )}
                <span className="text-right text-base tabular-nums">
                  {formatMoney(used)}
                  {c.budget ? (
                    <span className="text-muted-foreground">
                      {" "}
                      / {formatMoney(c.budget)}
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <span className="text-base font-semibold">By employee</span>
        <div className="rounded-lg border border-border bg-background">
          <div
            className={cn(
              COLS,
              "h-9 border-b border-border text-xs text-muted-foreground"
            )}
          >
            <span>Employee</span>
            <span>Case</span>
            <span>Cost center</span>
            <span className="text-right">Your share</span>
            <span className="text-right">Fees to date</span>
            <span className="text-right">Not yet billed</span>
          </div>
          {data.by_employee.length ? (
            data.by_employee.map((e, i) => (
              <div
                key={e.individual_id}
                className={cn(
                  COLS,
                  "h-12",
                  i < data.by_employee.length - 1 && "border-b border-border"
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold">
                    {initialsOf(e.name)}
                  </span>
                  <span className="truncate text-base font-medium">
                    {e.name}
                  </span>
                </span>
                <span className="truncate text-base">{e.case_label}</span>
                <span className="truncate font-mono text-xs">
                  {e.cost_center}
                </span>
                <span
                  className={cn(
                    "text-right text-base tabular-nums",
                    e.share < 100 ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {e.share}%
                </span>
                <span className="text-right text-base tabular-nums">
                  {dash(e.billed)}
                </span>
                <span
                  className={cn(
                    "text-right text-base tabular-nums",
                    !e.unbilled && "text-muted-foreground"
                  )}
                >
                  {dash(e.unbilled)}
                </span>
              </div>
            ))
          ) : (
            <div className="px-4 py-6 text-center text-base text-muted-foreground">
              No fees yet.
            </div>
          )}
        </div>
        {data.split_cases ? (
          <span className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            {data.split_cases === 1
              ? "One case is"
              : `${data.split_cases} cases are`}{" "}
            split with the employee. Only {me.company.name}&apos;s share is
            counted here; the firm bills the rest to the employee.
          </span>
        ) : null}
        <span
          className="flex items-start gap-1.5 text-xs text-muted-foreground"
          data-testid="spend-footnote"
        >
          <Info className="mt-0.5 size-3.5 shrink-0" />
          Estimates from {data.firm_name}&apos;s time and fees. Invoices come
          from the firm and can differ after adjustments.
        </span>
      </div>
    </>
  );
};
